import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

type LayerSidebarPanel =
  | 'add_external_layer'
  | 'add_internal_layer'
  | 'add_dhs_layer'
  | 'add_default_layer'
  | 'add_owm_layer'
  | 'add_category'
  | 'edit_layer'
  | 'review_queue'
  | 'draft_new_version'
  | 'edit_layer_version';

type LayerSidebarPanelContextType = {
  activePanel: LayerSidebarPanel | null;
  setActivePanel: (panel: LayerSidebarPanel | null) => void;
  togglePanel: (panel: LayerSidebarPanel) => void;
};

const LayerSidebarPanelContext = createContext<
  LayerSidebarPanelContextType | undefined
>(undefined);

export function useLayerSidebarPanelOptional() {
  return useContext(LayerSidebarPanelContext);
}

export function useLayerSidebarPanel() {
  const context = useContext(LayerSidebarPanelContext);
  if (context === undefined) {
    throw new Error(
      'useLayerSidebarPanel must be used within a LayerSidebarPanelProvider'
    );
  }

  return context;
}

export type LayerSidebarPanelProviderProps = {
  children: ReactNode;
};

export function LayerSidebarPanelProvider({
  children,
}: LayerSidebarPanelProviderProps) {
  const [activePanel, setActivePanel] = useState<LayerSidebarPanel | null>(
    null
  );

  const togglePanel = useCallback((panel: LayerSidebarPanel) => {
    setActivePanel((current) => (current === panel ? null : panel));
  }, []);

  return (
    <LayerSidebarPanelContext.Provider
      value={{
        activePanel,
        setActivePanel,
        togglePanel,
      }}
    >
      {children}
    </LayerSidebarPanelContext.Provider>
  );
}
